import { cn } from '@/shared/helpers/classnames'
import { LoadingIcon } from '@/shared/ui/icon'
import { HLayout, VLayout } from '@/shared/ui/layouts/flex'

import type { LoaderProps } from './types'

/**
 * Loader - Component that displays a loading indicator with an optional label
 *
 * @example
 * // Simple loader without label
 * <Loader />
 *
 * @example
 * // Loader with label (vertical layout)
 * <Loader label="Loading data..." size="lg" />
 *
 * @example
 * // Horizontal loader
 * <Loader label="Loading..." layout="horizontal" size="md" />
 *
 * @param label Optional text to display next to the loading icon
 * @param size Size of the loading icon (sm, md, lg)
 * @param layout Layout direction for the loader (vertical or horizontal)
 */
export const Loader = ({ label, size = 'md', layout = 'vertical' }: LoaderProps) => {
  const Layout = layout === 'horizontal' ? HLayout : VLayout

  return (
    <Layout gap="2" items="center" justify="center">
      <LoadingIcon size={size} />
      {label && (
        <span
          className={cn('text-base-content/70', {
            'text-sm': size === 'sm',
            'text-base': size === 'md',
            'text-lg': size === 'lg',
          })}
        >
          {label}
        </span>
      )}
    </Layout>
  )
}

Loader.displayName = 'Loader'
